import type { AlertActionRequest, CloseDispositionRead } from "@/alerts/types/alerts-workspace";
import { AlertStatus } from "@/alerts/types/machine-alert";

/**
 * The two verdicts an operator can give an alert.
 *
 * "False" is not a status of its own in the API model — it is
 * `status: Ignored` carrying `disposition: "false_alert"`. Naming it here keeps
 * that encoding in one place instead of spread across every button.
 */
export type AlertVerdict = "resolved" | "false";

/** Whether an alert already carries the given verdict. */
export const alertHasVerdict = (
  status: AlertStatus,
  disposition: CloseDispositionRead | null,
  verdict: AlertVerdict,
): boolean =>
  verdict === "resolved"
    ? status === AlertStatus.Resolved
    : status === AlertStatus.Ignored && disposition === "false_alert";

/**
 * The workflow command that moves an alert to `verdict`, or null when it is
 * already there.
 *
 * A FIRST decision and a CORRECTION are different commands, and the server
 * rejects the wrong one: while the alert is Pending the operator is deciding
 * (`confirm_signal` / `close_alert`), and once a decision exists they are
 * correcting it (`correct_result`, which states what it is correcting TO). The
 * map used to send a bare status PATCH for all four, which the compatibility
 * endpoint accepts only for the first decision — so corrections silently could
 * not be made (MIN-270).
 *
 * Null for "already that verdict" is deliberate: re-submitting the decision an
 * alert already carries is not a correction, it is a no-op that would burn a
 * workflow version and read as activity in the audit trail.
 */
export const alertDecisionAction = (
  status: AlertStatus,
  disposition: CloseDispositionRead | null,
  verdict: AlertVerdict,
): AlertActionRequest | null => {
  if (alertHasVerdict(status, disposition, verdict)) return null;

  if (status === AlertStatus.Pending) {
    return verdict === "resolved"
      ? { command: "confirm_signal" }
      : { command: "close_alert", disposition: "false_alert" };
  }

  // A decision already exists — anything else is a correction. This covers
  // Ignored-for-another-reason (`insufficient_evidence`) too: moving that to
  // "False" is still a correction, of the disposition rather than the status.
  return verdict === "resolved"
    ? { command: "correct_result", target_result: "confirmed_signal" }
    : { command: "correct_result", target_result: "closed", disposition: "false_alert" };
};

/**
 * The verdict an operator may still give, for building the controls.
 *
 * Pending offers both. A decided alert offers only the OTHER one — the ticket's
 * "do not allow submitting the result that is already selected", expressed once
 * here rather than as a disabled-button rule in each surface.
 */
export const availableVerdicts = (
  status: AlertStatus,
  disposition: CloseDispositionRead | null,
): AlertVerdict[] =>
  (["resolved", "false"] as const).filter(
    (verdict) => !alertHasVerdict(status, disposition, verdict),
  );
